const PROVINCES = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Northwest Territories': 'NT',
  'Nova Scotia': 'NS',
  Nunavut: 'NU',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Saskatchewan: 'SK',
  Yukon: 'YT'
};

const POSTAL_CODE_PATTERN = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

export const CANADIAN_PROVINCES = Object.keys(PROVINCES);

export const normalizeCanadianTaxAddress = (value) => {
  const address = value && typeof value === 'object' ? value : {};
  const province = String(address.province || address.state || '').trim();
  const provinceName = Object.keys(PROVINCES).find(
    (name) => name.toLowerCase() === province.toLowerCase()
  );
  const provinceCode = Object.entries(PROVINCES).find(
    ([, code]) => code.toLowerCase() === province.toLowerCase()
  )?.[0];
  const resolvedProvince = provinceName || provinceCode;
  const line1 = String(address.line1 || '').trim();
  const line2 = String(address.line2 || '').trim();
  const city = String(address.city || '').trim();
  const postalCode = String(address.postal_code || address.postalCode || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');

  if (String(address.country || 'CA').trim().toUpperCase() !== 'CA') {
    throw new Error('Enter a Canadian address.');
  }

  if (!resolvedProvince) {
    throw new Error('Choose a supported Canadian province or territory.');
  }

  if (!line1 || !city || !POSTAL_CODE_PATTERN.test(postalCode)) {
    throw new Error('Enter a street address, city, and valid Canadian postal code.');
  }

  return {
    line1,
    line2,
    city,
    province: resolvedProvince,
    postal_code: postalCode,
    country: 'CA'
  };
};

export const toStripeTaxAddress = (value) => {
  const address = normalizeCanadianTaxAddress(value);

  return {
    line1: address.line1,
    ...(address.line2 ? { line2: address.line2 } : {}),
    city: address.city,
    state: PROVINCES[address.province],
    postal_code: address.postal_code,
    country: address.country
  };
};

export const resolveCanadianTaxLocation = ({
  deliveryMethod,
  pickupAddress,
  deliveryAddress
}) => {
  if (deliveryMethod === 'pickup') {
    return {
      address: normalizeCanadianTaxAddress(pickupAddress),
      addressSource: 'pickup'
    };
  }

  if (deliveryMethod === 'delivery') {
    return {
      address: normalizeCanadianTaxAddress(deliveryAddress),
      addressSource: 'shipping'
    };
  }

  throw new Error('Choose pickup or delivery before calculating tax.');
};

export const buildStripeTaxCalculationParams = ({
  orderId,
  lineItems,
  address,
  shipFromAddress
}) => {
  const params = {
    currency: 'cad',
    'customer_details[address_source]': 'shipping'
  };
  const stripeAddress = toStripeTaxAddress(address);
  const stripeShipFromAddress = toStripeTaxAddress(shipFromAddress);

  Object.entries(stripeAddress).forEach(([key, value]) => {
    params[`customer_details[address][${key}]`] = value;
  });

  Object.entries(stripeShipFromAddress).forEach(([key, value]) => {
    params[`ship_from_details[address][${key}]`] = value;
  });

  lineItems.forEach((item, index) => {
    if (!Number.isSafeInteger(item.amount) || item.amount < 0) {
      throw new Error('A tax calculation line item amount is invalid.');
    }

    if (!/^txcd_[0-9]+$/.test(String(item.taxCode || ''))) {
      throw new Error('A tax calculation line item is missing its Stripe tax code.');
    }

    params[`line_items[${index}][amount]`] = String(item.amount);
    params[`line_items[${index}][reference]`] = `${orderId}-${index + 1}`;
    params[`line_items[${index}][tax_code]`] = item.taxCode;
    params[`line_items[${index}][tax_behavior]`] = 'exclusive';
  });

  return params;
};
